import { useSubmitVetterJobPricesMutation, type CurrencyEnum } from '@codezo/graphql';
import { SectionHead } from '@codezo/quote-detail';
import { ArrowLeftRight, Check, Loader2, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { formatDate } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';
import { JobNotice } from '../JobShell';
import {
	buildSubmission,
	clearedForm,
	formFromJob,
	type PriceFormErrors,
	type PriceFormState,
	type VetterJob,
} from './price-form';
import { TierPriceTable } from './TierPriceTable';

interface PricesSectionProps {
	job: VetterJob;
	/** Refetches the job; the form resets from what the server now holds. */
	onSubmitted: () => Promise<unknown>;
}

/**
 * The supplier's prices for each product and quantity. The products
 * themselves are drawn above, in `JobProducts`; this is only the form.
 *
 * Prices are in the currency selected in the top bar, like every figure on
 * the platform. A submission REPLACES the previous one, so the form always
 * carries every tier: what was sent before is prefilled, and clearing a row is
 * how a tier stops being priced.
 */
export function PricesSection({ job, onSubmitted }: PricesSectionProps) {
	const { selectedCurrency: currency, setCurrency } = useCurrencyStore();
	const [form, setForm] = useState<PriceFormState>(() => formFromJob(job));
	/** Set once the supplier chose to clear the old figures and re-enter them. */
	const [reentering, setReentering] = useState(false);
	const [errors, setErrors] = useState<PriceFormErrors>({});
	const [submit, { loading }] = useSubmitVetterJobPricesMutation();

	/* Figures already sent in another currency than the one selected. Showing
	   them under the new label would misstate every number, and sending them
	   would reprice the job by accident — so the form stops and asks. */
	const sentIn = job.submittedAt && job.currency ? job.currency : null;
	const mismatch = !!sentIn && sentIn !== currency && !reentering;

	const blocked = !job.canSubmitPrices;
	const disabled = loading || blocked || mismatch;

	const handleSubmit = async () => {
		const { errors: next, lines } = buildSubmission(form, currency, job);
		setErrors(next);
		if (Object.keys(next).length > 0) return;
		try {
			const { data } = await submit({
				variables: {
					input: {
						// Required from an account (which sees many jobs), optional
						// from a link session — sent always, so both work.
						vetterJobId: job.id,
						currency,
						note: form.note.trim() || null,
						lines,
					},
				},
			});
			const res = data?.submitVetterJobPrices;
			if (!res?.success) {
				setErrors({ form: res?.message || 'Your prices could not be saved.' });
				// The studio changed its delivery options meanwhile: load the current
				// ones, so the form shows what can actually be priced.
				if (/no longer on this offer/i.test(res?.message ?? '')) await onSubmitted();
				return;
			}
			toast.success(job.submittedAt ? 'Prices updated' : 'Prices sent');
			await onSubmitted();
		} catch (err) {
			setErrors({
				form: err instanceof Error ? err.message : 'Your prices could not be saved.',
			});
		}
	};

	return (
		<section className="flex flex-col gap-4">
			<SectionHead
				title="Your prices"
				meta={`your cost in ${currency} · leave a quantity empty if you don't quote it`}
			/>

			{mismatch && sentIn && (
				<div className="flex flex-wrap items-center gap-3 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-4 py-3 text-sm">
					<ArrowLeftRight size={15} className="shrink-0 text-text-3" />
					<span className="min-w-0 flex-1 text-text-2">
						You sent these prices in <b className="font-medium">{sentIn}</b>, and the
						platform is showing {currency}.
					</span>
					<Button size="sm" onClick={() => setCurrency(sentIn as CurrencyEnum)}>
						Switch to {sentIn}
					</Button>
					<Button
						size="sm"
						variant="outline"
						onClick={() => {
							setForm((f) => clearedForm(f, job));
							setReentering(true);
						}}
					>
						Re-enter in {currency}
					</Button>
				</div>
			)}

			{job.submittedAt && !mismatch && (
				<div className="flex items-center gap-2 text-sm text-text-3">
					<Check size={14} className="text-status-success" />
					Sent {formatDate(job.submittedAt)}. You can change and resend them until the
					request is closed.
				</div>
			)}

			{job.items.map((item) => (
				<div
					key={item.orderQuoteItemId}
					className="flex flex-col gap-2 rounded-[var(--r-3)] border border-line-1 bg-surface p-4"
				>
					<div className="flex items-baseline gap-2">
						<span className="text-base font-medium text-text-1">
							{item.productName ?? 'Unnamed product'}
						</span>
						{item.productSku && (
							<span className="font-mono text-xs text-text-4">{item.productSku}</span>
						)}
					</div>
					<TierPriceTable
						tiers={item.tiers}
						drafts={form.tiers}
						currency={currency}
						errors={errors}
						disabled={disabled}
						onChange={(tierId, draft) =>
							setForm((f) => ({ ...f, tiers: { ...f.tiers, [tierId]: draft } }))
						}
					/>
				</div>
			))}

			<div className="flex flex-col gap-1.5">
				<Label>Note to 05 Creation (optional)</Label>
				<Textarea
					value={form.note}
					onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
					placeholder="Minimums, assumptions, how long these prices are valid…"
					rows={3}
					disabled={disabled}
				/>
			</div>

			{errors.form && <JobNotice title={errors.form} />}
			{Object.keys(errors).some((k) => k !== 'form') && (
				<JobNotice title="Some prices need a look" detail="Fix the fields marked above." />
			)}

			<div className="flex flex-col items-end gap-1.5">
				<Button onClick={handleSubmit} disabled={disabled}>
					{loading ? (
						<Loader2 size={14} data-icon="inline-start" className="animate-spin" />
					) : (
						<Send size={14} data-icon="inline-start" />
					)}
					{job.submittedAt ? 'Update prices' : 'Send prices'}
				</Button>
				{blocked && job.submitPricesBlockedReason ? (
					<span className="text-sm text-text-4">{job.submitPricesBlockedReason}</span>
				) : (
					mismatch &&
					sentIn && (
						<span className="text-sm text-text-4">
							Switch to {sentIn} or re-enter your prices in {currency} first.
						</span>
					)
				)}
			</div>
		</section>
	);
}
